// The Instructor panel (pitch 10b): opened from the "INSTRUCTOR" chip in the scenario stage's dials
// strip. Presets swap the whole ruleset in one tap; six sliders tune the constants the independent
// review flagged as invisible TypeScript ("lethality scalar, break threshold, C2 (command and control)
// base minutes, EW (electronic warfare) factor, UAS (unmanned aircraft system) loss, detection night
// multiplier") with a plain-language line for what each one does. Every change lands in
// `store.dials.rules` as `{preset, overrides}` and reaches the sim through `SimClient.init` (see
// src/sim/worker.ts's 'init' handler, which resolves it with `loadRules`).
import { JevToggle } from './JevToggle';
import { useStore } from '../state/store';
import { deepMergeRules, loadRules, rulesOverridesNonEmpty, type Rules, type RulesOverrides, type RulesPreset } from '../sim/rules';
import { Chip } from './cards/ui';

const PRESET_INFO: { value: RulesPreset; label: string; blurb: string }[] = [
  { value: 'training', label: 'training', blurb: 'Forgiving: lethality x0.6, units break at 15% morale instead of 25% - a run built to teach the deck, not to punish a slow plan.' },
  { value: 'realistic', label: 'realistic', blurb: 'The default ruleset every other number on this page is measured against.' },
  { value: 'ukraine-2024', label: 'ukraine-2024', blurb: 'Heavier UAS attrition, more lethal fires, stronger EW (electronic warfare), and wider detection - a harder, more current fight.' },
];

interface SliderSpec {
  key: string;
  title: string;
  min: number;
  max: number;
  step: number;
  get: (r: Rules) => number;
  patch: (value: number) => RulesOverrides;
  effect: (value: number) => string;
  format: (value: number) => string;
}

const SLIDERS: SliderSpec[] = [
  {
    key: 'lethality',
    title: 'Lethality scalar',
    min: 0.02,
    max: 0.08,
    step: 0.005,
    get: (r) => r.lethalityScalar,
    patch: (v) => ({ lethalityScalar: v }),
    effect: (v) => `Direct-fire damage scales by this on every hit. Higher = engagements resolve faster and platoons break sooner; lower = a more forgiving, slower grind.`,
    format: (v) => `x${v.toFixed(3)}`,
  },
  {
    key: 'break',
    title: 'Break threshold',
    min: 10,
    max: 40,
    step: 1,
    get: (r) => r.breakThreshold,
    patch: (v) => ({ breakThreshold: v }),
    effect: () => `A unit below this morale withdraws toward its nearest headquarters. Higher = units break early and often; lower = they hold on longer before running.`,
    format: (v) => `${Math.round(v)}`,
  },
  {
    key: 'c2min',
    title: 'C2 base minutes (company)',
    min: 1,
    max: 8,
    step: 0.5,
    get: (r) => r.c2.companyBaseMin,
    patch: (v) => ({ c2: { companyBaseMin: v } }),
    effect: () => `Base transmission delay, before EW (electronic warfare) and contact factors, for an order to a company-type maneuver unit. Higher = every order takes longer to arrive.`,
    format: (v) => `${v.toFixed(1)} min`,
  },
  {
    key: 'ew',
    title: 'EW factor (moderate)',
    min: 1,
    max: 3.5,
    step: 0.1,
    get: (r) => r.c2.ewFactor.moderate,
    patch: (v) => ({ c2: { ewFactor: { moderate: v } } }),
    effect: () => `Multiplies C2 (command and control) delay under moderate electronic warfare. Higher = orders crawl once the enemy starts jamming.`,
    format: (v) => `x${v.toFixed(1)}`,
  },
  {
    key: 'uasloss',
    title: 'UAS loss (moderate EW)',
    min: 0.02,
    max: 0.3,
    step: 0.01,
    get: (r) => r.uasLossByEw.moderate,
    patch: (v) => ({ uasLossByEw: { moderate: v } }),
    effect: () => `Per-minute chance a flying UAS (unmanned aircraft system) airframe is shot down under moderate jamming. Higher = drones do not last long overhead.`,
    format: (v) => `${Math.round(v * 100)}%/min`,
  },
  {
    key: 'nightdet',
    title: 'Detection night multiplier',
    min: 0.2,
    max: 1,
    step: 0.05,
    get: (r) => r.detection.todNight,
    patch: (v) => ({ detection: { todNight: v } }),
    effect: () => `Scales detection probability at night. Higher = night fights are nearly as visible as day; lower = a real advantage for whoever attacks in the dark.`,
    format: (v) => `x${v.toFixed(2)}`,
  },
];

export function rulesetLabel(preset: RulesPreset, overrides: RulesOverrides | undefined): string {
  return `Ruleset: ${preset}${rulesOverridesNonEmpty(overrides) ? ' (modified)' : ''}`;
}

export function InstructorPanel({ onClose }: { onClose: () => void }) {
  const dials = useStore((s) => s.dials);
  const setDials = useStore((s) => s.setDials);
  const preset = dials.rules.preset;
  const overrides = dials.rules.overrides;
  const effective = loadRules(preset, overrides);

  const choosePreset = (p: RulesPreset): void => {
    setDials({ rules: { preset: p, overrides: undefined } });
  };

  const changeSlider = (spec: SliderSpec, value: number): void => {
    const merged = deepMergeRules((overrides ?? {}) as unknown as Record<string, unknown>, spec.patch(value) as unknown as Record<string, unknown>);
    setDials({ rules: { preset, overrides: merged as RulesOverrides } });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-6"
      onClick={onClose}
    >
      <div
        data-testid="instructor-panel"
        className="thin-scroll mt-8 w-full max-w-[720px] rounded-sm border border-slate-700 bg-slate-900 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-slate-700 pb-3">
          <span className="mono text-[13px] uppercase tracking-[0.25em] text-paper">Instructor</span>
          <span className="mono text-[11px] text-fog">{rulesetLabel(preset, overrides)}</span>
          <span className="flex-1" />
          <Chip onClick={onClose} testId="instructor-close" title="Close the instructor panel">close</Chip>
        </div>

        <p className="mt-3 text-[12px] leading-relaxed text-fog">
          Every tuning constant the sim runs on is data, not hidden TypeScript (data/rules.json) - a
          planner can see it and argue with it. Pick a preset, or move a slider; either way the change is
          recorded on this run and shown in the debrief as "{rulesetLabel(preset, overrides)}".
        </p>

        <div className="mt-4">
          <div className="mono text-[10px] uppercase tracking-[0.15em] text-paper">Presets</div>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {PRESET_INFO.map((p) => (
              <Chip
                key={p.value}
                active={p.value === preset}
                onClick={() => choosePreset(p.value)}
                testId={`instructor-preset-${p.value}`}
                title={p.blurb}
              >
                {p.label}
              </Chip>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] leading-snug text-fog">
            {PRESET_INFO.find((p) => p.value === preset)?.blurb}
          </p>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {SLIDERS.map((spec) => {
            const value = spec.get(effective);
            return (
              <div key={spec.key} className="wt-card" data-testid={`instructor-slider-${spec.key}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="mono text-[10px] uppercase tracking-[0.12em] text-paper">{spec.title}</span>
                  <span className="mono text-[11px] text-paper">{spec.format(value)}</span>
                </div>
                <input
                  type="range"
                  min={spec.min}
                  max={spec.max}
                  step={spec.step}
                  value={value}
                  onChange={(e) => changeSlider(spec, Number(e.target.value))}
                  className="mt-1.5 w-full accent-[var(--color-friendly)]"
                  data-testid={`instructor-slider-${spec.key}-input`}
                  aria-label={spec.title}
                />
                <p className="mt-1 text-[10px] leading-snug text-fog">{spec.effect(value)}</p>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-4"><JevToggle /></div>
      </div>
  );
}
